export type RepeatMode = "off" | "all" | "one";

export interface SoundtrackTrack {
  id: string;
  title: string;
  stage: string;
  url: string;
  seconds: number;
  kbps: number;
}

export const SOUNDTRACK: SoundtrackTrack[] = [
  {
    id: "menu",
    title: "Select Your Champion",
    stage: "Title Screen",
    url: "/assets/audio/menu-select-your-champion.mp3?v=2",
    seconds: 89.21,
    kbps: 96
  },
  {
    id: "level01",
    title: "Mutant Mayhem",
    stage: "The Last Block",
    url: "/assets/audio/level01-mutant-mayhem.mp3",
    seconds: 166.21,
    kbps: 96
  },
  {
    id: "level01-boss",
    title: "Glitch Boss",
    stage: "The Last Block",
    url: "/assets/audio/level01-sam-altman-glitch-boss.mp3",
    seconds: 150.49,
    kbps: 128
  },
  {
    id: "level02",
    title: "Neon Brawler",
    stage: "The Launch Yard",
    url: "/assets/audio/level02-neon-brawler.mp3",
    seconds: 172.01,
    kbps: 96
  },
  {
    id: "level02-boss",
    title: "Neon Inferno",
    stage: "The Launch Yard",
    url: "/assets/audio/level02-elon-neon-inferno.mp3",
    seconds: 120.93,
    kbps: 192
  },
  {
    id: "level03",
    title: "Gladiator Grid",
    stage: "The Corner Office",
    url: "/assets/audio/level03-gladiator-grid.mp3",
    seconds: 135.73,
    kbps: 192
  },
  {
    id: "level03-boss",
    title: "Corner Office",
    stage: "The Corner Office",
    url: "/assets/audio/level03-dario-boss.mp3",
    seconds: 108.41,
    kbps: 192
  },
  {
    id: "level03-final",
    title: "Final Form",
    stage: "The Corner Office",
    url: "/assets/audio/level03-dario-final-form.mp3",
    seconds: 198.81,
    kbps: 192
  },
  {
    id: "level04",
    title: "Horizon Home",
    stage: "Horizon Home",
    url: "/assets/audio/level04-horizon-home.mp3",
    seconds: 169.57,
    kbps: 96
  },
  {
    id: "level04-boss",
    title: "Metaverse",
    stage: "Horizon Home",
    url: "/assets/audio/level04-zuckerberg-metaverse.mp3",
    seconds: 149.17,
    kbps: 96
  }
];

export const EQ_BANDS = [60, 170, 310, 600, 1000, 3000, 6000, 12000, 14000, 16000] as const;

export const EQ_PRESETS: Record<string, number[]> = {
  Flat: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  Dance: [7, 5, 2, 0, -2, 0, 3, 5, 6, 5],
  Rock: [5, 3, -1, -2, -1, 2, 4, 5, 5, 4],
  Jazz: [3, 2, 1, 2, -1, -1, 0, 2, 3, 2],
  Bass: [9, 7, 4, 1, -2, -3, -1, 0, 1, 2]
};

export function formatTrackTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const remain = total % 60;
  return `${minutes}:${String(remain).padStart(2, "0")}`;
}

export function formatEqLabel(hz: number): string {
  return hz >= 1000 ? `${Math.round(hz / 1000)}K` : String(hz);
}

export function nextTrackIndex(
  current: number,
  count: number,
  shuffle: boolean,
  random: () => number = Math.random
): number {
  if (count <= 1) return 0;
  const index = clampIndex(current, count);
  if (!shuffle) return (index + 1) % count;
  let next = index;
  while (next === index) next = Math.floor(random() * count);
  return next;
}

export function previousTrackIndex(
  current: number,
  count: number,
  shuffle: boolean,
  random: () => number = Math.random
): number {
  if (count <= 1) return 0;
  const index = clampIndex(current, count);
  if (!shuffle) return (index - 1 + count) % count;
  return nextTrackIndex(index, count, true, random);
}

export function trackIndexAfterEnd(
  current: number,
  count: number,
  repeat: RepeatMode,
  shuffle: boolean,
  random: () => number = Math.random
): number | null {
  if (count <= 0) return null;
  if (repeat === "one") return clampIndex(current, count);
  if (!shuffle && repeat === "off" && clampIndex(current, count) >= count - 1) return null;
  return nextTrackIndex(current, count, shuffle, random);
}

function clampIndex(current: number, count: number): number {
  if (count <= 0) return 0;
  const index = Math.floor(current);
  if (!Number.isFinite(index) || index < 0) return 0;
  return Math.min(index, count - 1);
}
